/**
 * Cache-aware wrapper around navigator.geolocation. getCachedGeolocation only
 * reads the persisted coords and never prompts, requestGeolocation prompts and
 * belongs to a shopper action ("Use my location"). Both resolve to null on
 * explicit deny / API unavailable / storage blocked so callers can fall back
 * to IPStack.
 */
const COORDS_KEY = "geolocationCoords";
const DENIED_KEY = "geolocationDenied";

let inFlight = null;

function readLocal(key) {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    console.error(err);
    return null;
  }
}

function writeLocal(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.error(err);
  }
}

function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (err) {
    console.error(err);
    return null;
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (err) {
    console.error(err);
  }
}

function readCoords() {
  const raw = readLocal(COORDS_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Number.isFinite(parsed.latitude) && Number.isFinite(parsed.longitude)) {
      return { latitude: parsed.latitude, longitude: parsed.longitude };
    }
    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Persist coords in the shared cache format so the IPStack fallback writes
 * the same shape this module reads.
 */
export function persistCoords({ latitude, longitude }) {
  writeLocal(COORDS_KEY, JSON.stringify({ latitude, longitude }));
}

async function promptForCoords(positionOptions) {
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, positionOptions);
    });
    const { latitude, longitude } = position.coords;
    persistCoords({ latitude, longitude });
    return { latitude, longitude };
  } catch (err) {
    console.error(err);
    // Only suppress on explicit denial; TIMEOUT (3) and POSITION_UNAVAILABLE (2) are transient.
    if (err && err.code === 1) {
      writeSession(DENIED_KEY, "1");
    }
    return null;
  }
}

async function getCachedGeolocation() {
  return readCoords();
}

export async function requestGeolocation(positionOptions) {
  if (readSession(DENIED_KEY) === "1") {
    return null;
  }
  if (!("geolocation" in navigator)) {
    return null;
  }
  if (inFlight) {
    return inFlight;
  }
  inFlight = promptForCoords(positionOptions).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export async function canRequestGeolocation() {
  if (readSession(DENIED_KEY) === "1") {
    return false;
  }
  if (!("geolocation" in navigator)) {
    return false;
  }
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state !== "denied";
  } catch (err) {
    // Safari before 16 has no Permissions API for geolocation.
    return true;
  }
}

export default getCachedGeolocation;
